import { Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCandidateFaction } from '@/hooks/useCandidateFaction'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import type { FactionStatus } from '@/types'

export interface CandidateFactionBadgeProps {
  candidateId: string
  size?: 'sm' | 'md'
  showName?: boolean
  onClick?: () => void
  className?: string
}

const STATUS_COLORS: Record<FactionStatus, string> = {
  dormant: 'bg-slate-400',
  active: 'bg-blue-400',
  demanding: 'bg-amber-400',
  threatening: 'bg-orange-400',
  defecting: 'bg-red-400',
}

const STATUS_LABELS: Record<FactionStatus, string> = {
  dormant: 'Dormant',
  active: 'Active',
  demanding: 'Demanding',
  threatening: 'Threatening',
  defecting: 'Defecting',
}

const SIZE_CONFIG = {
  sm: {
    dot: 'h-2 w-2',
    text: 'text-[10px]',
    icon: 'h-2.5 w-2.5',
    maxWidth: 'max-w-[60px]',
  },
  md: {
    dot: 'h-2.5 w-2.5',
    text: 'text-xs',
    icon: 'h-3 w-3',
    maxWidth: 'max-w-[80px]',
  },
}

/**
 * Small badge indicating a candidate's faction membership.
 * Renders nothing if the candidate is not in a faction.
 */
export function CandidateFactionBadge({
  candidateId,
  size = 'sm',
  showName = false,
  onClick,
  className,
}: CandidateFactionBadgeProps) {
  const { faction, isLoading } = useCandidateFaction(candidateId)
  const sizeConfig = SIZE_CONFIG[size]

  // Don't render anything if no faction or loading
  if (isLoading || !faction) {
    return null
  }

  const statusColor = STATUS_COLORS[faction.status]
  const statusLabel = STATUS_LABELS[faction.status]

  const badgeContent = (
    <div
      className={cn(
        'inline-flex items-center gap-1',
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      {/* Status indicator dot */}
      <div className={cn('rounded-full shrink-0', sizeConfig.dot, statusColor)} />

      {/* Faction name (truncated) */}
      {showName && (
        <span
          className={cn(
            'truncate text-muted-foreground',
            sizeConfig.text,
            sizeConfig.maxWidth
          )}
        >
          {faction.name}
        </span>
      )}
    </div>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badgeContent}</TooltipTrigger>
      <TooltipContent>
        <div className="flex items-center gap-2">
          <Users className="h-3 w-3" />
          <span className="font-medium">{faction.name}</span>
          <span className="text-muted-foreground">• {statusLabel}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Simple dot indicator for use in tighter spaces.
 * Shows just the colored dot with tooltip.
 */
export interface FactionDotIndicatorProps {
  status: FactionStatus
  factionName: string
  onClick?: () => void
  className?: string
}

export function FactionDotIndicator({
  status,
  factionName,
  onClick,
  className,
}: FactionDotIndicatorProps) {
  const statusColor = STATUS_COLORS[status]
  const statusLabel = STATUS_LABELS[status]

  const dot = (
    <div
      className={cn(
        'h-2 w-2 rounded-full shrink-0',
        statusColor,
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    />
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{dot}</TooltipTrigger>
      <TooltipContent>
        <div className="flex items-center gap-2">
          <Users className="h-3 w-3" />
          <span className="font-medium">{factionName}</span>
          <span className="text-muted-foreground">• {statusLabel}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
